import { useEffect, useState } from 'react';

export type SeasonalIntroPhase = 'idle' | 'in' | 'out';

/**
 * Which seasonal layers App.tsx may mount: nothing before the lobby is shown, the curtain
 * alone while it plays, then the ambient effects (bats), then the cat.
 */
export type SeasonalStage = 'none' | 'intro' | 'ambient' | 'all';

interface SeasonalState {
  phase: SeasonalIntroPhase;
  stage: SeasonalStage;
}

const ATTRIBUTE = 'data-seasonal-branding';
// Brandings that play a curtain (HalloweenIntro / ChristmasIntro in App.tsx) when they switch on.
const INTRO_BRANDINGS: string[] = ['halloween', 'christmas'];

// Intro timeline in ms: the overlay fades in, data-seasonal-branding is stamped underneath it
// (section frost, christmas section icons), then the overlay fades out.
const STAMP_AT = 700;
const FADE_OUT_AT = 2600;
const DONE_AT = 3500;
// Mount stagger, keeping the seasonal work out of the boot window (section mount, thumbnail
// decode, first jackpot tick): ambient effects mount once the full-screen curtain layer is
// gone, the cat (clip fetch + video decoder init) another beat later.
const AMBIENT_AT = DONE_AT;
const CAT_AT = DONE_AT + 1500;
// The same stagger for lobby shows without a curtain (return from a game, intro already played,
// reduced motion). Shorter: there is no full-screen overlay to wait out, only the section-mount /
// thumbnail-decode burst, so the ambient effects follow the lobby by a beat and the cat by two.
const NO_INTRO_AMBIENT_AT = 1500;
const NO_INTRO_CAT_AT = 3000;

const stamp = (branding: string | undefined): void => {
  const root = document.documentElement;
  if (branding) {
    root.setAttribute(ATTRIBUTE, branding);
  } else {
    root.removeAttribute(ATTRIBUTE);
  }
};

// Remembers which branding's intro already played this tab session, so the lobby coming back from
// a game doesn't replay the curtain (and its layer memory) while the game's WebGL may still be in flight.
const INTRO_PLAYED_KEY = 'lobby.seasonalIntroPlayed';

// sessionStorage can throw (Safari private mode); a failed read falls back to playing the intro.
const hasIntroPlayed = (branding: string): boolean => {
  try {
    return sessionStorage.getItem(INTRO_PLAYED_KEY) === branding;
  } catch {
    return false;
  }
};

const markIntroPlayed = (branding: string): void => {
  try {
    sessionStorage.setItem(INTRO_PLAYED_KEY, branding);
  } catch {
    // ignore
  }
};

// Called when the user switches branding off in settings, so switching it back on plays the curtain again.
export const resetSeasonalIntroPlayed = (): void => {
  try {
    sessionStorage.removeItem(INTRO_PLAYED_KEY);
  } catch {
    // ignore
  }
};

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Stamps `data-seasonal-branding` on <html> so the seasonal dressing CSS (section frost,
 * christmas section icons) reaches everything, portals included, and plays the seasonal intro
 * once per session per branding with a curtain (halloween, christmas), when it switches on while
 * the lobby is up (boot, the settings toggle, the QA panel): the stamp is held back until the
 * overlay covers the screen, lands while it is up, and the overlay then fades away. Later lobby
 * shows (returning from a game) stamp immediately; switching branding off in settings resets the
 * memory so switching it back on plays the curtain again. No palette is involved —
 * the lobby colours stay stock. Skipped under prefers-reduced-motion.
 *
 * Also paces the seasonal mounts (`stage`): nothing until the lobby is shown; with an intro the
 * curtain first, the ambient effects at AMBIENT_AT and the cat at CAT_AT; without one the ambient
 * effects at NO_INTRO_AMBIENT_AT and the cat at NO_INTRO_CAT_AT.
 */
export const useSeasonalPalette = (seasonalBranding: string | undefined, lobbyReady: boolean): SeasonalState => {
  const [phase, setPhase] = useState<SeasonalIntroPhase>('idle');
  const [stage, setStage] = useState<SeasonalStage>('none');

  useEffect(() => {
const playIntro =
      seasonalBranding !== undefined &&
      INTRO_BRANDINGS.includes(seasonalBranding) &&
      lobbyReady &&
      !prefersReducedMotion() &&
      !hasIntroPlayed(seasonalBranding);

    if (!playIntro) {
      stamp(seasonalBranding);
      setPhase('idle');
      setStage('none');
      if (seasonalBranding === undefined || !lobbyReady) {
        return undefined;
      }

      const noIntroAmbientTimer = window.setTimeout(() => setStage('ambient'), NO_INTRO_AMBIENT_AT);
      const noIntroCatTimer = window.setTimeout(() => setStage('all'), NO_INTRO_CAT_AT);

      return () => {
        window.clearTimeout(noIntroAmbientTimer);
        window.clearTimeout(noIntroCatTimer);
      };
    }

    // Marked at start, not at stamp: a hide inside the first STAMP_AT ms must not replay it on return.
    markIntroPlayed(seasonalBranding);
    setPhase('in');
    setStage('intro');
    const switchTimer = window.setTimeout(() => stamp(seasonalBranding), STAMP_AT);
    const fadeOutTimer = window.setTimeout(() => setPhase('out'), FADE_OUT_AT);
    const doneTimer = window.setTimeout(() => setPhase('idle'), DONE_AT);
    const ambientTimer = window.setTimeout(() => setStage('ambient'), AMBIENT_AT);
    const catTimer = window.setTimeout(() => setStage('all'), CAT_AT);

    return () => {
      window.clearTimeout(switchTimer);
      window.clearTimeout(fadeOutTimer);
      window.clearTimeout(doneTimer);
      window.clearTimeout(ambientTimer);
      window.clearTimeout(catTimer);
    };
  }, [seasonalBranding, lobbyReady]);

  useEffect(() => () => stamp(undefined), []);

  return { phase, stage };
};
